import { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { push } from 'redux-first-history';

import { Heading, Page } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';

import { getMarketTrends } from 'app/shared/flux/actions/AreaActions';
import { useAppDispatch } from 'app/shared/flux/hooks';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { decorate } from 'app/shared/models/TopMarkets';
import StringConstants from 'app/shared/constants/StringConstants';
import { emitAreaSearchEvent, emitMarketTrendsStartPageViewEvent } from 'app/shared/constants/MarketTrendsCSEvents';

import SearchMarkets from 'app/shared/components/SearchMarkets';
import TopMarkets from 'app/shared/components/TopMarkets';
import UpsellCarouselModule from 'app/shared/modules/UpsellCarouselModule';

import type { RegionSuggestionsDto } from '@zg-rentals/ts-rental-growth-api-client';
import type { State } from 'app/shared/types/State';

const MarketTrendsPage = () => {
  const dispatch = useAppDispatch();
  const user = useSelector((state: State) => state.user);
  const { isMobile, screenWidth } = useSelector((state: State) => state.app.device);
  const topMarkets = useSelector((state: State) => state.topMarkets);
  const pathname = useSelector((state: State) => state.router.location.pathname);
  const queryString = useSelector((state: State) => state.router.location.search);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    emitMarketTrendsStartPageViewEvent(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = useCallback((region: RegionSuggestionsDto) => {
    async function getMarketTrendsAndNavigate() {
      const { resourceId } = region;
      if (resourceId) {
        setIsLoading(true);
        try {
          await dispatch(getMarketTrends({ resourceId }));
          emitAreaSearchEvent(user);
          setIsLoading(false);
          dispatch(push(`${pathsManifest.MarketTrendsPage}/${resourceId}/`));
        } catch (err) {
          setIsLoading(false);
          setError('An error occurred. Try again.');
          logError({
            error: err,
            errorType: 'MarketTrends',
            context: {
              details: 'Market Stats Page: Error fetching Market Stats (CSR)',
              root: 'pages/MarketTrendsPage#submit#getMarketTrendsAndNavigate',
            },
          });
        }
      }
    }
    getMarketTrendsAndNavigate();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const topRentalMarkets = topMarkets.topRentalMarkets.map((market) => decorate(market, queryString));
  const isSmallScreen = isMobile || screenWidth === 'md';

  return (
    <>
      <Helmet
        meta={[
          {
            name: 'description',
            content:
              'Use Zillow Rental Manager to compare rent across locations and see current and historical rental market data compiled by the leading real estate marketplace.',
          },
        ]}
        title="Rent Comparison Tool & Rental Market Trends Data | Zillow Rental Manager"
        link={[{ rel: 'canonical', href: `${StringConstants.ZILLOW_URL}${pathname}` }]}
      />
      <Page.Root css={{ backgroundColor: 'bg.screen.neutral' }}>
        <Page.Content>
          <Heading level={1} textStyle="responsive.heading-lg" fontFamily="heading-alt" css={{ marginTop: '600' }}>
            Rental Market Trends
          </Heading>
          <SearchMarkets submit={submit} isLoading={isLoading} error={error} />
          <TopMarkets markets={topRentalMarkets} isSmallScreen={isSmallScreen} isMobile={isMobile} />
          <UpsellCarouselModule addMarginTop={true} />
        </Page.Content>
      </Page.Root>
    </>
  );
};

export default MarketTrendsPage;
